import React from "react";
import { Link } from "react-router-dom";
import StaticPage from "./StaticPage";
import {
  BookOpen, Library, ShieldCheck, ScrollText, Layers, Timer,
  ArrowRight, LogIn, CheckCircle2, PhoneCall, Sparkles,
} from "lucide-react";

const CORE = [
  {
    icon: BookOpen,
    title: "Academic Books",
    body: "Prescribed textbooks, workbooks and reference guides — sourced, coordinated and distributed with a single point of contact for your school.",
  },
  {
    icon: Library,
    title: "Library Books",
    body: "Curated storybooks, encyclopaedias and reading collections for school libraries — reviewed, ordered and tracked through SchoolLane.",
  },
];

const HOW_WE_HELP = [
  {
    icon: Layers,
    title: "One centralised workflow",
    body: "Move book requisitions, approvals, purchase orders, deliveries and invoices out of email threads and WhatsApp groups into a single, structured platform.",
  },
  {
    icon: ShieldCheck,
    title: "Keep your existing suppliers",
    body: "SchoolLane is designed to support your school — not replace your trusted publishers and distributors. Continue working with the vendors you already know while we handle the coordination.",
  },
  {
    icon: Timer,
    title: "Save administrative hours",
    body: "Reduce the time your staff spends chasing quotations, follow-ups and delivery status — so they can focus on academics and students.",
  },
  {
    icon: ScrollText,
    title: "Complete audit trail",
    body: "Every requisition, approval and purchase order is time-stamped and reviewable. Board-ready records without the paperwork.",
  },
];

const BOARDS = ["ICSE", "ISC", "CBSE", "State Boards"];

export default function Schools() {
  return (
    <StaticPage
      title="Book Procurement, Simplified for Schools"
      subtitle="SchoolLane is your procurement and distribution partner for academic and library books — freeing your team from vendor coordination and paperwork."
    >
      {/* Core focus cards */}
      <div className="not-prose grid md:grid-cols-2 gap-6 mt-2">
        {CORE.map((c) => (
          <div key={c.title}
               className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-md transition-all duration-300">
            <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
              <c.icon size={22} />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <h3 className="font-heading font-semibold text-lg text-slate-900">
                {c.title}
              </h3>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>

      {/* Boards */}
      <div className="not-prose mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-7">
        <div className="text-xs uppercase tracking-widest font-semibold text-blue-700">
          Boards supported
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {BOARDS.map((b) => (
            <span key={b}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                             bg-white border border-slate-200 text-sm font-medium text-slate-700">
              <CheckCircle2 size={14} className="text-emerald-600" /> {b}
            </span>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Support may vary based on school requirements, curriculum and supplier availability.
        </p>
      </div>

      {/* How SchoolLane helps */}
      <div className="not-prose mt-14">
        <div className="text-xs font-semibold text-blue-700 tracking-wider uppercase">
          How SchoolLane helps
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">
          Everything book-related, in one workflow
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {HOW_WE_HELP.map((f) => (
            <div key={f.title}
                 className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
                <f.icon size={20} />
              </div>
              <h3 className="mt-4 font-heading font-semibold text-slate-900">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Roadmap */}
      <div className="not-prose mt-14 rounded-2xl border border-blue-100 bg-blue-50 p-6 sm:p-7 flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-white text-blue-700 grid place-items-center shrink-0">
          <Sparkles size={20} />
        </div>
        <div>
          <div className="font-heading font-semibold text-slate-900">
            What's next
          </div>
          <p className="mt-1 text-sm text-slate-700 leading-relaxed">
            SchoolLane's current focus is <strong>academic books</strong> and{" "}
            <strong>library books</strong>. Additional school procurement categories
            will be introduced in future phases, based on partner-school
            requirements. Founding partners get first access to every category we roll out.
          </p>
        </div>
      </div>

      {/* CTAs */}
      <div className="not-prose mt-14 rounded-2xl bg-slate-900 text-white p-8 sm:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-300">
            Ready to simplify book procurement?
          </div>
          <div className="mt-1 text-2xl font-heading font-semibold">
            Talk to us about your school's book procurement workflow.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" data-testid="schools-cta-consult"
                className="px-5 py-3 rounded-md bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors duration-200 inline-flex items-center gap-2">
            Request Procurement Consultation <ArrowRight size={16} />
          </Link>
          <Link to="/contact" data-testid="schools-cta-demo"
                className="px-5 py-3 rounded-md border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors duration-200 inline-flex items-center gap-2">
            <PhoneCall size={16} /> Book a Demo
          </Link>
          <Link to="/login" data-testid="schools-cta-login"
                className="px-5 py-3 rounded-md border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors duration-200 inline-flex items-center gap-2">
            <LogIn size={16} /> School Login
          </Link>
        </div>
      </div>
    </StaticPage>
  );
}
